import { GoogleOutlined, FacebookOutlined } from '@ant-design/icons';
import { BlobServiceClient } from '@azure/storage-blob'
import jwt_decode from 'jwt-decode'
import { token } from 'stylis'
import { format } from 'date-fns';
import { store } from './redux/store'


let routerHistory: { [key: string]: any } = {
  navigate: null,
  location: null,
}

export const getAppBaseUrl = (): string => {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const publicUrl = process.env.REACT_APP_WIZR_B2C_REDIRECT_URI
  if (!publicUrl || publicUrl === '.') return origin
  if (publicUrl.startsWith('http://') || publicUrl.startsWith('https://')) {
    try {
      return new URL(publicUrl).origin
    } catch {
      return origin
    }
  }
  const path = publicUrl.endsWith('/') ? publicUrl.slice(0, -1) : publicUrl
  return origin + (path.startsWith('/') ? path : '/' + path)
}
interface ITokenDecoded {
  iss: string
}

const extractIdToken = (tokenString = '') => tokenString ? tokenString.slice(tokenString.indexOf('=') + 1) : ''

export const buildAuthUrl = async (): Promise<string> => {
  const base = process.env.REACT_APP_API_BASE_URL
  const endpoint = process.env.REACT_APP_WIZR_B2C_AUTH_ENDPOINT
  const p = process.env.REACT_APP_WIZR_B2C_P
  const clientId = process.env.REACT_APP_WIZR_B2C_CLIENT_ID
  const redirectUri = process.env.REACT_APP_WIZR_B2C_REDIRECT_URI
//adding condition to check for aws or azure base url and return the auth url accordingly
  if (base === 'https://apitwo.wizrai.com/') {
    const verifier = generateCodeVerifier()
    const challenge = await generateCodeChallenge(verifier)
    localStorage.setItem('pkce_code_verifier', verifier)
    // Mark PKCE flow as in-flight before redirecting so the session-expired event
    // is suppressed when the page reloads with ?code= and API calls fire before
    // exchangeCodeForTokens runs (sessionStorage persists across tab navigations).
    sessionStorage.setItem('pkce_in_progress', '1')
    return `${endpoint}?p=${p}&client_id=${clientId}&redirect_uri=${redirectUri}&nonce=defaultNonce&scope=openid+offline_access&response_type=code&code_challenge=${challenge}&code_challenge_method=S256&prompt=login`
  }

  return `${endpoint}?p=${p}&client_id=${clientId}&redirect_uri=${redirectUri}&nonce=defaultNonce&scope=openid&response_type=id_token&prompt=login`
}

export const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32)
  window.crypto.getRandomValues(array)
  return btoa(Array.from(array, (b) => String.fromCharCode(b)).join(''))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

export const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await window.crypto.subtle.digest('SHA-256', data)
  return btoa(Array.from(new Uint8Array(digest), (b) => String.fromCharCode(b)).join(''))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

const convertBytestoMB = (sizeInBytes: number) =>
  sizeInBytes ? `${(sizeInBytes / 1024 ** 2)?.toFixed(2)} MB` : '0 MB'

const setRouterHistory = (history: any) => (routerHistory = history)
const generateAzureUploadUrl = (tenantId: string) => {
  const azureSASUrl =
    process.env.REACT_APP_AZURE_FILE_CONTAINER_URL +
    '/' +
    tenantId +
    '?' +
    process.env.REACT_APP_AZURE_FILE_CONTAINER_SAS_TOKEN
  return azureSASUrl
}

const downloadBlob = async (folderId: any, fileData: any) => {
  try {
    //const blobServiceClient = new BlobServiceClient(process.env.REACT_APP_AZURE_SAS_URL || '')
    const blobServiceClient = new BlobServiceClient(
      generateAzureUploadUrl('d8971718-5aad-480c-8dd0-eecf50e93a93') || '',
    )
    const containerClientUrl = `/inarchive/${folderId}`
    const containerClient = blobServiceClient.getContainerClient(containerClientUrl)
    const blobClient = containerClient.getBlobClient(fileData?.file_name)

    const response = await blobClient.download()

    const blob = await response.blobBody
    return blob
  } catch (error) {
    console.error('Error downloading blob:', error)
  }
}

import { getApiAuthHeaders } from './utils/apiAuthHeaders'

const statusChecker = async (url: string) => {
  const checkStatus = async (): Promise<any> => {
    const statusResponse = await fetch(url, { headers: getApiAuthHeaders() })
    const statusResponseJson = await statusResponse.json()
    if (statusResponseJson.runtimeStatus === "Running" || statusResponseJson.runtimeStatus === "Pending") {
      await new Promise((resolve) => setTimeout(resolve, 5000))
      const value = await checkStatus()
      return value
    } else if (statusResponseJson.runtimeStatus === "Failed") {
      return {
        output: statusResponseJson?.output,
        runtimeStatus: statusResponseJson.runtimeStatus,
        error: statusResponseJson?.output?.error || "Operation failed"
      }
    } else if (statusResponseJson.runtimeStatus === "Terminated") {
      return {
        output: statusResponseJson?.output,
        runtimeStatus: statusResponseJson.runtimeStatus,
        error: "Operation was terminated"
      }
    } else {
      localStorage.setItem('prompt_history_id', statusResponseJson?.output?.prompt_history_id)
      return {
        output: statusResponseJson?.output,
        runtimeStatus: statusResponseJson.runtimeStatus
      }
    }
  }
  if (url) {
    const response = await checkStatus()
    return response
  }
}

const statusCheckerWithAuth = statusChecker

const PaginationLimit = 25
const getValueFromLocalStorage = (key: string) => {
  const localStorageValue = localStorage.getItem(key)
  if (localStorageValue !== null && localStorageValue !== '') {
    try {
      return JSON.parse(localStorageValue)
    } catch (error) {
      console.error('Error parsing JSON from localStorage:', error)
      return null
    }
  } else {
    return null
  }
}

const addValueToLocalStorage = (key: string, jsonValue: any) =>
  localStorage.setItem(key, JSON.stringify(jsonValue))

const uploadToLocalStorage = (filesNames: any) => {
  const excistingFiles = getFromLocalStorage()
  if (excistingFiles?.length > 0) {
    const updatedFiles = [...excistingFiles, ...filesNames]
    localStorage.setItem('filesToUpload', JSON.stringify(updatedFiles))
  } else {
    localStorage.setItem('filesToUpload', JSON.stringify(filesNames))
  }
}

const getAccessLists = (token: string): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    try {
      const tokenDecoded: any = jwt_decode<ITokenDecoded>(token);

      if (typeof tokenDecoded === 'object' && tokenDecoded !== null) {
        const tokenUserAccessList = tokenDecoded?.extension_UserAccess;
        const arrayOfPermissions = tokenUserAccessList.split(", ");
        resolve(arrayOfPermissions);
      } else {
        resolve([]); // or resolve with any default value
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      reject(error); // Reject the promise with the error
    }
  });
};

const getTenantId = () => {
  let tenantId
  const token = localStorage.getItem('id_token')

  if (token && token !== 'null' && token !== 'undefined') {
    try {
      const tokenDecoded: any = jwt_decode<ITokenDecoded>(token)
      if (typeof tokenDecoded === 'object' && tokenDecoded !== null) {

        // const tokenDecodedSplitted =tokenDecoded.iss
        // const uuidpattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
        // tenantId  = tokenDecodedSplitted.match(uuidpattern);
        tenantId = tokenDecoded.extension_TenantId
        //tenantId = tokenDecodedSplitted?.[3]
      }
    } catch (error) {
      console.error('Error decoding token in getTenantId:', error)
      tenantId = null
    }
  }
  return tenantId
}

const getTenantIdwithtokenId = (token: any) => {
  let tenantId
  // const token = localStorage.getItem('id_token')
  if (token && typeof token === 'string' && token.length > 0) {
    try {
      const tokenDecoded: any = jwt_decode<ITokenDecoded>(token)
      if (typeof tokenDecoded === 'object' && tokenDecoded !== null) {

        // const tokenDecodedSplitted =tokenDecoded.iss
        // const uuidpattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
        // tenantId  = tokenDecodedSplitted.match(uuidpattern);
        tenantId = tokenDecoded.extension_TenantId
        //tenantId = tokenDecodedSplitted?.[3]
      }
    } catch (error) {
      console.error('Error decoding token in getTenantIdwithtokenId:', error)
      tenantId = null
    }
  }
  return tenantId
}

const getUserRole = () => {
  let tenantId
  const token = localStorage.getItem('id_token')
  if (token) {
    const tokenDecoded: any = jwt_decode<ITokenDecoded>(token)
    if (typeof tokenDecoded === 'object' && tokenDecoded !== null) {

      // const tokenDecodedSplitted =tokenDecoded.iss
      // const uuidpattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
      // tenantId  = tokenDecodedSplitted.match(uuidpattern);
      tenantId = tokenDecoded.extension_UserRole
      //tenantId = tokenDecodedSplitted?.[3]
    }
  }
  return tenantId
}

const getUserRoleFromToken = (token: any) => {
  let tenantId
  // const token = localStorage.getItem('id_token')
  if (token) {
    const tokenDecoded: any = jwt_decode<ITokenDecoded>(token)
    if (typeof tokenDecoded === 'object' && tokenDecoded !== null) {

      // const tokenDecodedSplitted =tokenDecoded.iss
      // const uuidpattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
      // tenantId  = tokenDecodedSplitted.match(uuidpattern);
      tenantId = tokenDecoded.extension_UserRole
      //tenantId = tokenDecodedSplitted?.[3]
    }
  }
  return tenantId
}

const getTenantNameFromToken = (token: any) => {
  let tenantId
  // const token = localStorage.getItem('id_token')
  if (token) {
    const tokenDecoded: any = jwt_decode<ITokenDecoded>(token)
    if (typeof tokenDecoded === 'object' && tokenDecoded !== null) {

      // const tokenDecodedSplitted =tokenDecoded.iss
      // const uuidpattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
      // tenantId  = tokenDecodedSplitted.match(uuidpattern);
      tenantId = tokenDecoded.extension_UserRole
      //tenantId = tokenDecodedSplitted?.[3]
    }
  }
  return tenantId
}



const getFromLocalStorage = () => {
  const data = JSON.parse(localStorage.getItem('filesToUpload') || ' {}')
  return data
}
const getFileType = (file_name: string) => {
  try {
    const [, file_extension] = file_name.split('.')
    const lowerCaseExtension = file_extension.toLowerCase()

    const fileTypes: Record<string, string> = {
      '.txt': 'TEXT',
      '.pdf': 'PDF',
      '.csv': 'CSV',
      '.docx': 'WORD',
      '.doc': 'WORD',
      '.ppt': 'POWERPOINT',
      '.pptx': 'POWERPOINT',
      '.xls': 'EXCEL',
      '.xlsx': 'EXCEL',
      '.json': 'JSON',
      '.xml': 'XML',
      '.html': 'HTML',
      '.md': 'MARKDOWN',

      // Add more file types and extensions as needed
    }

    return fileTypes[`.${lowerCaseExtension}`] || 'Unknown'
  } catch (e) {
    console.error(`An error occurred: ${e}`)
    throw e
  }
}

function generateNodeSequenceResponse(req: any) {

  const inputEdges = req.edges;

  const mapping = inputEdges?.reduce((result: any, item: any) => {
    result[item.source] = item.target;
    return result;
  }, {});

  const nodeSequence = [];
  let current_node = 'start';
  while (current_node in mapping) {
    nodeSequence.push(current_node);
    current_node = mapping[current_node];
  }
  nodeSequence.push(current_node);

  return nodeSequence;
}

// Extract properties from JSON Schema recursively (for Parse JSON blocks)
function extractSchemaProperties(schema: any, prefix: string = '', maxDepth: number = 10, currentDepth: number = 0): string[] {
  const properties: string[] = [];

  // Prevent infinite recursion
  if (currentDepth >= maxDepth || !schema || typeof schema !== 'object') {
    return properties;
  }

  // If schema has properties (object type)
  if (schema.properties && typeof schema.properties === 'object') {
    Object.keys(schema.properties).forEach(key => {
      const propertyPath = prefix ? `${prefix}.${key}` : key;
      const propertySchema = schema.properties[key];

      // Add the property itself
      properties.push(propertyPath);

      // If property is an object, recursively extract nested properties
      if (propertySchema.type === 'object' && propertySchema.properties) {
        const nestedProperties = extractSchemaProperties(
          propertySchema,
          propertyPath,
          maxDepth,
          currentDepth + 1
        );
        properties.push(...nestedProperties);
      }
      // If property is an array, add the array itself but NOT individual item properties
      // (as items are dynamic and unknown until runtime)
      else if (propertySchema.type === 'array') {
        // Array itself is already added above
        // Do NOT recurse into items as they are dynamic
      }
    });
  }

  return properties;
}

// Generate parameter list including schema-based parameters from Parse JSON blocks and variables from Initialize Variables blocks
function generateParameterListWithSchema(nodes: any[], edges: any[], currentNodeId: string, startnodedata: any): string[] {
  const saveAppPayload = { edges, nodes };
  const sequence = generateNodeSequenceResponse(saveAppPayload);
  
  // Find elements before current node
  const index = sequence.indexOf(currentNodeId);
  const previousNodes = index === -1 ? sequence : sequence.slice(0, index);
  
  // Start with default parameters from Start node
  const defaultParameters = startnodedata?.config?.parameters?.map((param: any) => param?.name) || [];
  
  // Build list of available parameters
  const parameterList: string[] = [...defaultParameters];
  
  // Process each previous node
  previousNodes.slice(1).forEach((nodeId: string) => {
    const node = nodes.find((n: any) => n.id === nodeId);
    
    if (!node) return;
    
    // Add standard .output parameter for all nodes
    parameterList.push(`${nodeId}.output`);
    
    // Special handling for Parse JSON blocks (type: 'parsejson')
    if (node.type === 'parsejson' && node.data?.config?.schema) {
      try {
        // Parse schema if it's a string
        const schema = typeof node.data.config.schema === 'string'
          ? JSON.parse(node.data.config.schema)
          : node.data.config.schema;
        
        // Extract all properties from schema
        const schemaProperties = extractSchemaProperties(schema);
        
        // Add each property as {nodeId}.{propertyPath}
        schemaProperties.forEach((propertyPath: string) => {
          parameterList.push(`${nodeId}.${propertyPath}`);
        });
      } catch (error) {
        console.error(`Error parsing schema for node ${nodeId}:`, error);
        // If schema parsing fails, just add the .output parameter (already added above)
      }
    }
    
    // Special handling for Initialize Variables blocks (type: 'initialize_variables')
    if (node.type === 'initialize_variables' && node.data?.config?.variables) {
      try {
        const variables = node.data.config.variables;
        
        // Add each variable name as a parameter
        variables.forEach((variable: any) => {
          if (variable.name) {
            parameterList.push(variable.name);
          }
        });
      } catch (error) {
        console.error(`Error extracting variables for node ${nodeId}:`, error);
        // If variable extraction fails, continue without adding variables
      }
    }
  });
  
  return parameterList;
}

const dropDownConverter = (value: string[]) => {
  const result =
    value?.length > 0 &&
    value?.map((item: any, key: number) => ({
      label: "App Version " + item,
      value: item,
    }))
  return result
}

// CSV validation requirements for different upload types
const CSV_VALIDATION_REQUIREMENTS = {
  most_likely_questions: {
    requiredColumns: ['questions', 'score'],
    description: 'CSV must contain columns: "questions" and "score"',
    example: 'questions,score\n"What is your return policy?",5\n"How do I track my order?",3'
  },
  past_tickets: {
    requiredColumns: ['ticket_id', 'subject', 'category', 'priority', 'raised_by', 'created_on'],
    description: 'CSV must contain columns: "ticket_id", "subject", "category", "priority", "raised_by", "created_on"',
    example: 'ticket_id,subject,category,priority,raised_by,created_on\nT001,Login Issue,Technical,High,John Doe,2024-01-15'
  },
  faq: {
    requiredColumns: ['question', 'answer', 'category'],
    description: 'CSV must contain columns: "question", "answer", "category"',
    example: 'question,answer,category\n"What is your return policy?","30 days return policy",General'
  },
  bulk_run: {
    requiredColumns: ['input', 'expected_output'],
    description: 'CSV must contain columns: "input" and "expected_output"',
    example: 'input,expected_output\n"Hello","Hi! How can I help you today?"'
  }
}

const validateCSVFormat = (fileContent: string, uploadType: string) => {
  const { dispatch } = store ?? {}
  
  try {
    const lines = fileContent.split('\n').filter(line => line.trim())
    if (lines.length < 2) {
      return {
        isValid: false,
        error: 'CSV file must contain at least a header row and one data row'
      }
    }

    const headers = lines[0].split(',').map(header => header.trim().toLowerCase())
    const requirements = CSV_VALIDATION_REQUIREMENTS[uploadType as keyof typeof CSV_VALIDATION_REQUIREMENTS]
    
    if (!requirements) {
      return { isValid: true } // No specific requirements for this type
    }

    const missingColumns = requirements.requiredColumns.filter(
      requiredCol => !headers.includes(requiredCol.toLowerCase())
    )

    if (missingColumns.length > 0) {
      return {
        isValid: false,
        error: `${requirements.description}. Missing columns: ${missingColumns.join(', ')}`
      }
    }

    return { isValid: true }
  } catch (error) {
    return {
      isValid: false,
      error: 'Invalid CSV format. Please check the file structure.'
    }
  }
}

const uploadCheck = (type?: any, fileInfo?: any) => {  
  const { dispatch } = store ?? {}

  const MAX_IMAGE_SIZE_MB = 2;
  const MAX_AUDIO_SIZE_MB = 5;
  const MAX_DOCUMENT_SIZE_MB = 50;

  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
  const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav'];
  const ALLOWED_DOCUMENT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/csv',
    'text/xml',
    'text/plain',
    'text/html',
    'application/markdown',
    'text/markdown',
    'text/x-markdown',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ''
  ];

  const ALLOWED_GOOGLE_DRIVE_TYPES = [
    'application/pdf',
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.spreadsheet',
    'application/vnd.google-apps.presentation',
    'text/plain',
    'text/csv',
    'text/xml',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ];
  const ALLOWED_ONLY_CSV = [
    'text/csv'
  ]

  if (type === 5) {
    const maxCSVSizeBytes = MAX_DOCUMENT_SIZE_MB * 1024 * 1024;
    if (fileInfo?.size > maxCSVSizeBytes) {
      dispatch.commonModel.updateNotificationState({
        message: 'Upload Failed',
        description: `CSV file size (${(fileInfo?.size / (1024 * 1024)).toFixed(2)}MB) exceeds the 50MB limit`,
        type: 'failed',
      })
      return false;
    }
    if (!ALLOWED_ONLY_CSV.includes(fileInfo?.type)) {
      dispatch.commonModel.updateNotificationState({
        message: 'Invalid File Type',
        description: 'Only CSV files are allowed for this upload. Please select a .csv file.',
        type: 'failed',
      })
      return false;
    }
    else {
      return true
    }
  }else if (type === 6) {
    // check file size    
    const maxImageSizeBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024;
    if (fileInfo?.size > maxImageSizeBytes) {
      dispatch.commonModel.updateNotificationState({
        message: 'Upload Failed',
        description: `Image file size (${(fileInfo?.size / (1024 * 1024)).toFixed(2)}MB) exceeds the 2MB limit`,
        type: 'failed',
      })
      return false;
    }

    // Check file type
    if (!ALLOWED_IMAGE_TYPES.includes(fileInfo?.type)) {
      dispatch.commonModel.updateNotificationState({
        message: 'Invalid File Type',
        description: 'Only JPEG, PNG, and GIF images are allowed. Please select a valid image file.',
        type: 'failed',
      })
      return false;
    }
    else {
      return true
    }
  }else if (type === 7) { // Assuming type 7 represents audio files
    // Check file size for audio
    const maxAudioSizeBytes = MAX_AUDIO_SIZE_MB * 1024 * 1024;
    if (fileInfo?.size > maxAudioSizeBytes) {
      dispatch.commonModel.updateNotificationState({
        message: 'Upload Failed',
        description: `Audio file size (${(fileInfo?.size / (1024 * 1024)).toFixed(2)}MB) exceeds the 5MB limit`,
        type: 'failed',
      });
      console.error('Audio file size exceeds the 5MB limit');
      return false;
    }

    // Check file type for audio
    if (!ALLOWED_AUDIO_TYPES.includes(fileInfo?.type)) {
      dispatch.commonModel.updateNotificationState({
        message: 'Invalid File Type',
        description: 'Only MP3 and WAV audio files are allowed. Please select a valid audio file.',
        type: 'failed',
      });
      return false;
    }
    else {
      return true
    }
  } else if (type === 8) { // New condition for Google Drive files
    const maxDocSizeBytes = MAX_DOCUMENT_SIZE_MB * 1024 * 1024;

    // Allow files with size 0 for Google Drive
    if (fileInfo?.size > 0 && fileInfo?.size > maxDocSizeBytes) {
      dispatch.commonModel.updateNotificationState({
        message: 'Upload Failed',
        description: `Google Drive file size (${(fileInfo?.size / (1024 * 1024)).toFixed(2)}MB) exceeds the 50MB limit`,
        type: 'failed',
      });
      console.error('Google Drive file size exceeds the 50MB limit');
      return false;
    }

    // Check file type for Google Drive
    if (!ALLOWED_GOOGLE_DRIVE_TYPES.includes(fileInfo?.type)) {
      console.error('Invalid Google Drive file type:', fileInfo?.type);
      dispatch.commonModel.updateNotificationState({
        message: 'Invalid File Type',
        description: 'This file type is not supported for Google Drive integration. Please select a supported document type.',
        type: 'failed',
      });
      return false;
    } else {
      return true;
    }
  } else { // Existing condition for document files
    const maxDocSizeBytes = MAX_DOCUMENT_SIZE_MB * 1024 * 1024;
    if (fileInfo?.size > maxDocSizeBytes) {
      dispatch.commonModel.updateNotificationState({
        message: 'Upload Failed',
        description: `Document file size (${(fileInfo?.size / (1024 * 1024)).toFixed(2)}MB) exceeds the 50MB limit`,
        type: 'failed',
      });
      console.error('Document file size exceeds the 50MB limit');
      return false;
    }

    // Check file type for document
    if (!ALLOWED_DOCUMENT_TYPES.includes(fileInfo?.type)) {
      console.error('Invalid document file type:', fileInfo?.type);
      dispatch.commonModel.updateNotificationState({
        message: 'Invalid File Type',
        description: 'This file type is not supported. Please select a valid document file (PDF, Word, PowerPoint, CSV, XML, or text file).',
        type: 'failed',
      });
      return false;
    } else {
      return true;
    }
  }
}

const FilterValues = [
  { label: '==', value: '$eq' },
  { label: '!=', value: '$ne' },
  { label: '>', value: '$gt' },
  { label: '>=', value: '$ge' },
  { label: '<', value: '$lt' },
  { label: '<=', value: '$le' },
  { label: 'in', value: '$in' },
  { label: 'not in', value: '$notin' },
]

const pastTickets = [
  {
    name: 'Default',
    desc: '',
    date: 'June 15, 11.39 PM'
  },
  {
    name: 'Tech Support',
    desc: '',
    date: 'June 15, 11.39 PM'
  }
  , {
    name: 'Sales',
    desc: '',
    date: 'June 15, 11.39 PM'
  }
]

const tickets = [
  {
    ticketId: 1,
    subject: "Website Down",
    category: "Technical",
    ticketPriority: "High",
    raisedBy: "John Doe",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 2,
    subject: "Email Configuration Issue",
    category: "Technical",
    ticketPriority: "Medium",
    raisedBy: "Jane Smith",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 3,
    subject: "Access Permission Request",
    category: "Administrative",
    ticketPriority: "Low",
    raisedBy: "Alice Johnson",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 1,
    subject: "Website Down",
    category: "Technical",
    ticketPriority: "High",
    raisedBy: "John Doe",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 2,
    subject: "Email Configuration Issue",
    category: "Technical",
    ticketPriority: "Medium",
    raisedBy: "Jane Smith",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 3,
    subject: "Access Permission Request",
    category: "Administrative",
    ticketPriority: "Low",
    raisedBy: "Alice Johnson",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 1,
    subject: "Website Down",
    category: "Technical",
    ticketPriority: "High",
    raisedBy: "John Doe",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 2,
    subject: "Email Configuration Issue",
    category: "Technical",
    ticketPriority: "Medium",
    raisedBy: "Jane Smith",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 3,
    subject: "Access Permission Request",
    category: "Administrative",
    ticketPriority: "Low",
    raisedBy: "Alice Johnson",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 1,
    subject: "Website Down",
    category: "Technical",
    ticketPriority: "High",
    raisedBy: "John Doe",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 2,
    subject: "Email Configuration Issue",
    category: "Technical",
    ticketPriority: "Medium",
    raisedBy: "Jane Smith",
    createdOn: "2024/01/19"
  },
  {
    ticketId: 3,
    subject: "Access Permission Request",
    category: "Administrative",
    ticketPriority: "Low",
    raisedBy: "Alice Johnson",
    createdOn: "2024/01/19"
  },
  // Add more objects as needed
];

const healthTable = [{
  customer: "Google",
  total_tickets: 175,
  open_tickets: 6,
  escalation: 35,
  time_for_resolve: 47,
  sentiment: 60,
  health: 20
},
{
  customer: "Facebook",
  total_tickets: 175,
  open_tickets: 6,
  escalation: 80,
  sentiment: 90,
  time_for_resolve: 4,
  health: 60
}]


const LanguageOptions = [
  { "display_name": "English", "code": "en_US" },
  { "display_name": "Afrikaans", "code": "af_ZA" },
  { "display_name": "Albanian", "code": "sq_AL" },
  { "display_name": "Arabic", "code": "ar_AE" },
  { "display_name": "Bengali", "code": "bn_BD" },
  { "display_name": "Bahasa Indonesia", "code": "in_ID" },
  { "display_name": "Bosnian", "code": "bs_BA" },
  { "display_name": "Bulgarian", "code": "bg_BG" },
  { "display_name": "Chinese (Simplified)", "code": "zh_CN" },
  { "display_name": "Chinese (Traditional)", "code": "zh_HK" },
  { "display_name": "Croatian", "code": "hr_HR" },
  { "display_name": "Czech", "code": "cs_CZ" },
  { "display_name": "Danish", "code": "da_DK" },
  { "display_name": "Dutch", "code": "nl_NL" },
  { "display_name": "Estonian", "code": "et_EE" },
  { "display_name": "Espanol", "code": "es_ES" },
  { "display_name": "Filipino", "code": "tl_PH" },
  { "display_name": "Finnish", "code": "fi_FI" },
  { "display_name": "French", "code": "fr_FR" },
  { "display_name": "German", "code": "de_DE" },
  { "display_name": "Greek", "code": "el_GR" },
  { "display_name": "Gujarati", "code": "gu_IN" },
  { "display_name": "Hebrew", "code": "he_IL" },
  { "display_name": "Hindi", "code": "hi_IN" },
  { "display_name": "Hungarian", "code": "hu_HU" },
  { "display_name": "Italian", "code": "it_IT" },
  { "display_name": "Japanese", "code": "ja_JP" },
  { "display_name": "Kannada", "code": "kn_IN" },
  { "display_name": "Korean", "code": "ko_KR" },
  { "display_name": "Lithuanian", "code": "lt_LT" },
  { "display_name": "Malay", "code": "ms_MY" },
  { "display_name": "Malayalam", "code": "ml_IN" },
  { "display_name": "Marathi", "code": "mr_IN" },
  { "display_name": "Mongolian", "code": "mn_MN" },
  { "display_name": "Nepali", "code": "ne_NP" },
  { "display_name": "Norwegian", "code": "no_NO" },
  { "display_name": "Pasthu", "code": "ps_AF" },
  { "display_name": "Persian", "code": "fa_IR" },
  { "display_name": "Punjabi", "code": "pa_IN" },
  { "display_name": "Polish", "code": "pl_PL" },
  { "display_name": "Portugese", "code": "pt_PT" },
  { "display_name": "Romanian", "code": "ro_RO" },
  { "display_name": "Russian", "code": "ru_RU" },
  { "display_name": "Serbian", "code": "sr_RS" },
  { "display_name": "Slovak", "code": "sk_SK" },
  { "display_name": "Slovenian", "code": "sl_SI" },
  { "display_name": "Swahili", "code": "sw_TZ" },
  { "display_name": "Swedish", "code": "sv_SE" },
  { "display_name": "Tamil", "code": "ta_IN" },
  { "display_name": "Telugu", "code": "te_IN" },
  { "display_name": "Thai", "code": "th_TH" },
  { "display_name": "Turkish", "code": "tr_TR" },
  { "display_name": "Ukrainian", "code": "uk_UA" },
  { "display_name": "Urdu", "code": "ur_PK" },
  { "display_name": "Vietnamese", "code": "vi_VN" },
  { "display_name": "Welsh", "code": "cy_GB" },
  { "display_name": "Zulu", "code": "zu_ZA" }
]



function getAllValues() {

  return {
    currentMonthStartDate: 1577817000000,
    currentMonthEndDate: 1893436200000,
    previousMonthStartDate: 1577817000000,
    previousMonthEndDate: 1893436200000
  };
}

function getMonthRangeConversation() {
  const today = new Date();
  const thirtyOneDaysAgo = new Date();
  today.setHours(0, 0, 0, 0); // Normalize today to midnight for consistent date calculation

  // Subtract 31 days from the current date
  thirtyOneDaysAgo.setDate(today.getDate() - 31);


  const nextDay = new Date(today.getTime());
  nextDay.setDate(today.getDate() + 1);


  return {
    currentMonthStartDate: thirtyOneDaysAgo.getTime(),
    currentMonthEndDate: nextDay.getTime()
  };
}

function getMonthRange() {
  // const today = new Date();
  // const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

  // return {
  //     startDate: oneMonthAgo.getTime(),
  //     endDate: today.getTime()
  // };


  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize today to midnight for consistent date calculation

  const oneWeekAgo = new Date(today.getTime());
  oneWeekAgo.setDate(today.getDate() - 31); // Set to exactly one week ago

  const twoWeeksAgo = new Date(oneWeekAgo.getTime());
  twoWeeksAgo.setDate(oneWeekAgo.getDate() - 31); // Set to exactly two weeks ago

  const nextDay = new Date(today.getTime());
  nextDay.setDate(today.getDate() + 1);

  // const today = new Date();
  // const currentMonthStartDate = new Date(today.getFullYear(), today.getMonth(), 1); // First day of the current month
  // const currentMonthEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of the current month

  // const previousMonthStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1); // First day of the previous month
  // const previousMonthEndDate = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of the previous month

  // return {
  //   currentMonthStartDate: 1675189800000,
  //   currentMonthEndDate: 1682879400000,
  //   previousMonthStartDate: 1669833000000,
  //   previousMonthEndDate: 1675189800000
  // }
  return {
    currentMonthStartDate: oneWeekAgo.getTime(),
    currentMonthEndDate: nextDay.getTime(),
    previousMonthStartDate: twoWeeksAgo.getTime(),
    previousMonthEndDate: oneWeekAgo.getTime()
  };
}


// Returns the next 1 month window starting today (local time)
function getNextMonthRange() {
  // Show last 30 days up to end of today
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const start = new Date(today.getTime());
  start.setDate(start.getDate() - 30);
  start.setHours(0, 0, 0, 0);

  const end = today;

  // Previous 30-day window immediately before the current window
  const prevEnd = new Date(start.getTime() - 1);
  prevEnd.setHours(23, 59, 59, 999);
  const prevStart = new Date(prevEnd.getTime());
  prevStart.setDate(prevStart.getDate() - 30 + 1);
  prevStart.setHours(0, 0, 0, 0);

  return {
    currentMonthStartDate: start.getTime(),
    currentMonthEndDate: end.getTime(),
    previousMonthStartDate: prevStart.getTime(),
    previousMonthEndDate: prevEnd.getTime()
  };
}


function getThreeMonthRange() {
  // const today = new Date();
  // const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

  // return {
  //     startDate: oneMonthAgo.getTime(),
  //     endDate: today.getTime()
  // };
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize today to midnight for consistent date calculation

  const oneWeekAgo = new Date(today.getTime());
  oneWeekAgo.setDate(today.getDate() - 90); // Set to exactly one week ago

  const twoWeeksAgo = new Date(oneWeekAgo.getTime());
  twoWeeksAgo.setDate(oneWeekAgo.getDate() - 90); // Set to exactly two weeks ago

  const nextDay = new Date(today.getTime());
  nextDay.setDate(today.getDate() + 1);
  // const today = new Date();
  // const currentMonthStartDate = new Date(today.getFullYear(), today.getMonth()-2, 1); // First day of the current month
  // const currentMonthEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of the current month

  // const previousMonthStartDate = new Date(today.getFullYear(), today.getMonth() -1, 1); // First day of the previous month
  // const previousMonthEndDate = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of the previous month

  // return {
  //   currentMonthStartDate: 1675189800000,
  //   currentMonthEndDate: 1682879400000,
  //   previousMonthStartDate: 1669833000000,
  //   previousMonthEndDate: 1675189800000
  // }
  return {
    currentMonthStartDate: oneWeekAgo.getTime(),
    currentMonthEndDate: nextDay.getTime(),
    previousMonthStartDate: twoWeeksAgo.getTime(),
    previousMonthEndDate: oneWeekAgo.getTime()
  };
}



function getWeekRange() {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize today to midnight for consistent date calculation

  const oneWeekAgo = new Date(today.getTime());
  oneWeekAgo.setDate(today.getDate() - 7); // Set to exactly one week ago

  const twoWeeksAgo = new Date(oneWeekAgo.getTime());
  twoWeeksAgo.setDate(oneWeekAgo.getDate() - 7); // Set to exactly two weeks ago

  const nextDay = new Date(today.getTime());
  nextDay.setDate(today.getDate() + 1);

  // return {
  //   currentMonthStartDate: 1675189800000,
  //   currentMonthEndDate: 1677609000000,
  //   previousMonthStartDate: 1672511400000,
  //   previousMonthEndDate: 1675189800000
  // }
  return {
    currentMonthStartDate: oneWeekAgo.getTime(),
    currentMonthEndDate: nextDay.getTime(),
    previousMonthStartDate: twoWeeksAgo.getTime(),
    previousMonthEndDate: oneWeekAgo.getTime()
  };
}

function getDayRange() {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set today's date to midnight to ensure the full day is covered

  const oneDayAgo = new Date(today.getTime());
  oneDayAgo.setDate(today.getDate() - 1); // Set to exactly one day ago at midnight

  const twoDaysAgo = new Date(oneDayAgo.getTime());
  twoDaysAgo.setDate(oneDayAgo.getDate() - 1); // Set to two days ago from today at midnight

  const nextDay = new Date(today.getTime());
  nextDay.setDate(today.getDate() + 1);

  // return {
  //   currentMonthStartDate: 1676917800000,
  //   currentMonthEndDate: 1677609000000,
  //   previousMonthStartDate: 1676313000000,
  //   previousMonthEndDate: 1676917800000
  // }
  // return {
  //   currentMonthStartDate: oneDayAgo,  // Adjust as needed to return the correct start date
  //   currentMonthEndDate: nextDay       // Adjust to return the correct end date
  // };


  return {
    currentMonthStartDate: today.getTime(),
    currentMonthEndDate: nextDay.getTime(),
    previousMonthStartDate: twoDaysAgo.getTime(),
    previousMonthEndDate: oneDayAgo.getTime()
  };
}

function getYesterdayRange() {
  // Single-day window for yesterday (start to end of that day)
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);

  const endOfYesterday = new Date(yesterday.getTime());
  endOfYesterday.setHours(23, 59, 59, 999);

  // Previous single-day window (day before yesterday)
  const twoDaysAgoStart = new Date(yesterday.getTime());
  twoDaysAgoStart.setDate(twoDaysAgoStart.getDate() - 1);
  twoDaysAgoStart.setHours(0, 0, 0, 0);
  const twoDaysAgoEnd = new Date(twoDaysAgoStart.getTime());
  twoDaysAgoEnd.setHours(23, 59, 59, 999);

  return {
    currentMonthStartDate: yesterday.getTime(),
    currentMonthEndDate: endOfYesterday.getTime(),
    previousMonthStartDate: twoDaysAgoStart.getTime(),
    previousMonthEndDate: twoDaysAgoEnd.getTime()
  };
}

// Next day window from today -> tomorrow (end of day)
function getNextDayRange() {
  // Today only
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  // Previous day window
  const prevStart = new Date(start.getTime());
  prevStart.setDate(prevStart.getDate() - 1);
  prevStart.setHours(0, 0, 0, 0);
  const prevEnd = new Date(prevStart.getTime());
  prevEnd.setHours(23, 59, 59, 999);

  return {
    currentMonthStartDate: start.getTime(),
    currentMonthEndDate: end.getTime(),
    previousMonthStartDate: prevStart.getTime(),
    previousMonthEndDate: prevEnd.getTime()
  };
}

// Next week window from today -> +7 days (end of day)
function getNextWeekRange() {
  // Last 7 days including today
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end.getTime());
  start.setDate(start.getDate() - 7);
  start.setHours(0, 0, 0, 0);

  // Previous 7-day window
  const prevEnd = new Date(start.getTime() - 1);
  prevEnd.setHours(23, 59, 59, 999);
  const prevStart = new Date(prevEnd.getTime());
  prevStart.setDate(prevStart.getDate() - 7 + 1);
  prevStart.setHours(0, 0, 0, 0);

  return {
    currentMonthStartDate: start.getTime(),
    currentMonthEndDate: end.getTime(),
    previousMonthStartDate: prevStart.getTime(),
    previousMonthEndDate: prevEnd.getTime()
  };
}

// Next 3 months window from today -> +3 months (end of day)
function getNextThreeMonthRange() {
  // Last ~90 days including today
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end.getTime());
  start.setDate(start.getDate() - 90);
  start.setHours(0, 0, 0, 0);

  // Previous ~90-day window
  const prevEnd = new Date(start.getTime() - 1);
  prevEnd.setHours(23, 59, 59, 999);
  const prevStart = new Date(prevEnd.getTime());
  prevStart.setDate(prevStart.getDate() - 90 + 1);
  prevStart.setHours(0, 0, 0, 0);

  return {
    currentMonthStartDate: start.getTime(),
    currentMonthEndDate: end.getTime(),
    previousMonthStartDate: prevStart.getTime(),
    previousMonthEndDate: prevEnd.getTime()
  };
}

// Next year window from today -> +1 year (end of day)
function getNextYearRange() {
  // Last 1 year including today
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end.getTime());
  start.setFullYear(start.getFullYear() - 1);
  start.setHours(0, 0, 0, 0);

  // Previous 1-year window
  const prevEnd = new Date(start.getTime() - 1);
  prevEnd.setHours(23, 59, 59, 999);
  const prevStart = new Date(prevEnd.getTime());
  prevStart.setFullYear(prevStart.getFullYear() - 1);
  prevStart.setHours(0, 0, 0, 0);

  return {
    currentMonthStartDate: start.getTime(),
    currentMonthEndDate: end.getTime(),
    previousMonthStartDate: prevStart.getTime(),
    previousMonthEndDate: prevEnd.getTime()
  };
}


function getCurrentTimeChatBot() {
  const date = new Date();
  return format(date, 'dd MMM yyyy HH:mm');
}

function getCurrentPayloadTimeChatBot() {
  const date = new Date();
  return format(date, 'dd MMM yyyy HH:mm a');
}

function convertMillisToDateString(milliseconds: any) {
  // Create a new Date object with the given milliseconds
  const date = new Date(milliseconds);

  // Array of month names
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Get the day of the month
  const day = date.getDate();

  // Get the month name from the monthNames array
  const month = monthNames[date.getMonth()];

  // Get the last two digits of the year
  const year = date.getFullYear().toString().slice(-2);

  // Return the formatted string
  return `${day} ${month} ${year}`;
}

function getFormatedDate(timestamp: any) {
  const date = new Date(timestamp);

  // Format the date and time
  const formattedDate = date.toLocaleString('en-US', {
    month: 'long', // Full month name
    day: 'numeric', // Numeric day
    year: 'numeric', // Full year
    hour: 'numeric', // Hour in 12-hour format
    minute: 'numeric', // Minutes
    // second: 'numeric', // Seconds
    hour12: true // 12-hour format
  });

  return formattedDate.replace('at', ''); // Remove the comma after the day
}

const getQuestion = (question: string) => {

  const regex = new RegExp(
    `^Default language : [^;]+;(.+)$`
  );
  const match = question.match(regex);
  if (match) {
    return match[1].trim();
  } else {
    return question;
  }
};

const formatToPythonAcceptableName = (name: string) => {
  return name
    .replace(/[^a-zA-Z0-9]/g, '_');
}

const getLocalTimeFromUTC = (timestamp: string) => {
  // Parse UTC timestamp and convert to local time
  const utcDate = new Date(timestamp + 'Z'); // Adding 'Z' to ensure UTC interpretation

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  return utcDate.toLocaleString('en-US', options);
}

function convertUTCToLocalTime(utcTimestamp: any) {

  const dateUTC = new Date(utcTimestamp * 1000);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localTime = dateUTC.toLocaleString('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return {
    timeZone,
    localTime,
  };
}

// Example usage
const result = convertUTCToLocalTime(1749016690);

const convertLocalToUTC = (localTimestamp: string): string => {
  const localDate = new Date(localTimestamp);
  const utcYear = localDate.getUTCFullYear();
  const utcMonth = String(localDate.getUTCMonth() + 1).padStart(2, '0');
  const utcDay = String(localDate.getUTCDate()).padStart(2, '0');
  const utcHours = String(localDate.getUTCHours()).padStart(2, '0');
  const utcMinutes = String(localDate.getUTCMinutes()).padStart(2, '0');
  const utcSeconds = String(localDate.getUTCSeconds()).padStart(2, '0');

  return `${utcYear}-${utcMonth}-${utcDay}T${utcHours}:${utcMinutes}:${utcSeconds}Z`;
}
const convertTimeToUTC = (localTime: string): string => {
  // Split the time string into hours, minutes, and seconds
  const [hours, minutes, seconds] = localTime.split(':').map(Number);

  // Create a Date object for today with the given time
  const localDate = new Date();
  localDate.setHours(hours, minutes, seconds, 0);

  // Get the UTC time components
  const utcHours = String(localDate.getUTCHours()).padStart(2, '0');
  const utcMinutes = String(localDate.getUTCMinutes()).padStart(2, '0');
  const utcSeconds = String(localDate.getUTCSeconds()).padStart(2, '0');

  // Return the UTC time in "HH:mm:ss" format
  return `${utcHours}:${utcMinutes}:${utcSeconds}`;
}

const isToday = (date: any) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

// Helper function to check if a date is yesterday (after 12:00 AM)
const isYesterday = (date: any) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

const filteredData = (data: any) => {
  return {
    today: data.filter((item: any) => {
      const itemDate = new Date(item.time);
      return isToday(itemDate);
    }),
    yesterday: data.filter((item: any) => {
      const itemDate = new Date(item.time);
      return isYesterday(itemDate);
    }),
    others: data.filter((item: any) => {
      const itemDate = new Date(item.time);
      return !isToday(itemDate) && !isYesterday(itemDate);
    })
  }
}

function getYearRange() {
  const end = new Date();
  end.setUTCHours(23, 59, 59, 999);

  const start = new Date();
  start.setFullYear(start.getFullYear() - 1);
  start.setUTCHours(0, 0, 0, 0);

  return {
    currentMonthStartDate: start.getTime(),
    currentMonthEndDate: end.getTime()
  };
}

export function getCustomRange(start: number | Date, end: number | Date) {
  // Convert to timestamps if Date objects are passed
  const startTime = typeof start === 'number' ? start : start.getTime();
  const endTime = typeof end === 'number' ? end : end.getTime();

  return {
    currentMonthStartDate: startTime,
    currentMonthEndDate: endTime
  };
}

const getUserId = () => {
  let tenantId
  const token = localStorage.getItem('id_token')

  if (token && token !== 'null' && token !== 'undefined') {
    try {
      const tokenDecoded: any = jwt_decode<ITokenDecoded>(token)
      if (typeof tokenDecoded === 'object' && tokenDecoded !== null) {

        // const tokenDecodedSplitted =tokenDecoded.iss
        // const uuidpattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
        // tenantId  = tokenDecodedSplitted.match(uuidpattern);
        tenantId = tokenDecoded.oid
        //tenantId = tokenDecodedSplitted?.[3]
      }
    } catch (error) {
      console.error('Error decoding token in getUserId:', error)
      tenantId = null
    }
  }
  return tenantId
}


const getUserName = () => {
  let userName
  const token = localStorage.getItem('id_token')
  if (token && token !== 'null' && token !== 'undefined') {
    try {
      const tokenDecoded: any = jwt_decode<ITokenDecoded>(token)
      if (typeof tokenDecoded === 'object' && tokenDecoded !== null) {
        userName = tokenDecoded.name
      }
    } catch (error) {
      console.error('Error decoding token in getUserName:', error)
      userName = null
    }
  }
  return userName
}


function getQuarterRange() {
  const currentDate = new Date();
  const currentQuarter = Math.floor(currentDate.getMonth() / 3);

  // Calculate start of current quarter
  const startDate = new Date(currentDate.getFullYear(), currentQuarter * 3, 1);
  startDate.setHours(0, 0, 0, 0);

  // Calculate end of current quarter
  const endDate = new Date(currentDate.getFullYear(), (currentQuarter + 1) * 3, 0);
  endDate.setHours(23, 59, 59, 999);

  return {
    currentMonthStartDate: startDate.getTime(),
    currentMonthEndDate: endDate.getTime(),
    previousMonthStartDate: startDate.getTime(),
    previousMonthEndDate: endDate.getTime()
  };
}

const formatUTCTime = (date: Date) => {
  const isoString = date.toISOString()
  return isoString.replace(/\.\d{3}Z$/, (match) => match.replace('Z', '000'))
}

const alertsData = [
  {
    name: "Daily Sales Report",
    schedule: "Every day",
    lastRun: "Aug 07, 09:00 AM",
    enable: "Active",
    lastModified: "Aug 01, 09:30 AM",
  },
  {
    name: "Inactive Users Report",
    schedule: "Every 6 hours",
    lastRun: "Aug 06, 06:00 PM",
    enable: "Active",
    lastModified: "Aug 06, 02:15 PM",
  },
  {
    name: "New Signups Report",
    schedule: "Every week",
    lastRun: "Aug 07, 09:00 AM",
    enable: "Active",
    lastModified: "Aug 06, 09:00 AM",
  },
  {
    name: "Abandoned Cart Report",
    schedule: "Every month",
    lastRun: "Aug 07, 09:00 AM",
    enable: "Active",
    lastModified: "Aug 06, 02:15 PM",
  },
  {
    name: "New Signups Report",
    schedule: "Every 6 months",
    lastRun: "Aug 06, 06:00 PM",
    enable: "Active",
    lastModified: "Aug 01, 09:30 AM",
  },
  {
    name: "New Signups Report",
    schedule: "Every year",
    lastRun: "Aug 06, 06:00 PM",
    enable: "Deactivate",
    lastModified: "Aug 06, 02:15 PM",
  },
  {
    name: "Abandoned Cart Report",
    schedule: "Every 3 months",
    lastRun: "Aug 07, 09:00 AM",
    enable: "Active",
    lastModified: "Aug 06, 02:15 PM",
  },
  {
    name: "Zendesk",
    schedule: "Every day",
    lastRun: "Aug 07, 09:00 AM",
    enable: "Active",
    lastModified: "Aug 01, 01:45 PM",
  },
  {
    name: "Abandoned Cart Report",
    schedule: "Every 12 hours",
    lastRun: "Aug 02, 12:00 PM",
    enable: "Active",
    lastModified: "Aug 01, 09:30 AM",
  },
  {
    name: "New Signups Report",
    schedule: "Every day",
    lastRun: "Aug 06, 06:00 PM",
    enable: "Active",
    lastModified: "Aug 01, 01:45 PM",
  },
  {
    name: "New Signups Report",
    schedule: "Every 12 hours",
    lastRun: "Aug 02, 12:00 PM",
    enable: "Active",
    lastModified: "Aug 01, 01:45 PM",
  },
];


// Schedule interval options for reports and alerts
const scheduleIntervalOptions = [
  { label: 'Minute', value: 'minute' },
  { label: 'Hour', value: 'hour' },
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
  { label: 'Year', value: 'year' }
];

// Dropdown options for different time units
const monthOptions = [
  { label: 'Jan', value: 1 }, { label: 'Feb', value: 2 }, { label: 'Mar', value: 3 },
  { label: 'Apr', value: 4 }, { label: 'May', value: 5 }, { label: 'Jun', value: 6 },
  { label: 'Jul', value: 7 }, { label: 'Aug', value: 8 }, { label: 'Sep', value: 9 },
  { label: 'Oct', value: 10 }, { label: 'Nov', value: 11 }, { label: 'Dec', value: 12 }
];

const dayOfMonthOptions = Array.from({ length: 31 }, (_, i) => ({
  label: (i + 1).toString(),
  value: i + 1
}));

const dayOfWeekOptions = [
  { label: 'Monday', value: 1 }, { label: 'Tuesday', value: 2 }, { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 }, { label: 'Friday', value: 5 }, { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 0 }
];

const hourOptions = Array.from({ length: 24 }, (_, i) => ({
  label: i.toString(),
  value: i
}));

const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
  label: i.toString(),
  value: i
}));

// Parse cron expression to determine schedule interval and config
const parseCronExpression = (cronExpr: string) => {
  const parts = cronExpr.trim().split(' ');
  if (parts.length !== 5) return null;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Determine the schedule interval based on which parts are wildcards
  let interval = 'minute';
  let config: any = {};

  if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    // Every minute: * * * * *
    interval = 'minute';
  } else if (hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    // Every hour: {minute} * * * *
    interval = 'hour';
    config.minute = parseInt(minute) || 0;
  } else if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    // Daily: {minute} {hour} * * *
    interval = 'day';
    config.minute = parseInt(minute) || 0;
    config.hour = parseInt(hour) || 0;
  } else if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    // Weekly: {minute} {hour} * * {dayOfWeek}
    interval = 'week';
    config.minute = parseInt(minute) || 0;
    config.hour = parseInt(hour) || 0;
    config.dayOfWeek = parseInt(dayOfWeek) || 0;
  } else if (month === '*' && dayOfWeek === '*') {
    // Monthly: {minute} {hour} {dayOfMonth} * *
    interval = 'month';
    config.minute = parseInt(minute) || 0;
    config.hour = parseInt(hour) || 0;
    config.dayOfMonth = parseInt(dayOfMonth) || 1;
  } else if (dayOfWeek === '*') {
    // Yearly: {minute} {hour} {dayOfMonth} {month} *
    interval = 'year';
    config.minute = parseInt(minute) || 0;
    config.hour = parseInt(hour) || 0;
    config.dayOfMonth = parseInt(dayOfMonth) || 1;
    config.month = parseInt(month) || 1;
  }

  console.log(`Parsing cron "${cronExpr}":`, { interval, config });
  return { interval, config };
};

// Generate cron expression based on interval and config
const generateCronExpression = (scheduleInterval: string, scheduleConfig: any) => {
  const { minute, hour, dayOfMonth, month, dayOfWeek } = scheduleConfig;

  switch (scheduleInterval) {
    case 'minute':
      return `* * * * *`; // Every minute
    case 'hour':
      return `${minute} * * * *`; // Every hour at specified minute
    case 'day':
      return `${minute} ${hour} * * *`; // Daily at specified hour:minute
    case 'week':
      return `${minute} ${hour} * * ${dayOfWeek}`; // Weekly on specified day at hour:minute
    case 'month':
      return `${minute} ${hour} ${dayOfMonth} * *`; // Monthly on specified day at hour:minute
    case 'year':
      return `${minute} ${hour} ${dayOfMonth} ${month} *`; // Yearly on specified month/day at hour:minute
    default:
      return '0 0 * * *';
  }
};

// Convert cron expression to human-readable format
const cronToHumanReadable = (cronExpr: string): string => {
  if (!cronExpr || typeof cronExpr !== 'string') {
    return 'Invalid cron expression';
  }

  const parts = cronExpr.trim().split(' ');
  if (parts.length !== 5) {
    return 'Invalid cron format';
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Helper function to get ordinal suffix
  const getOrdinal = (n: number): string => {
    const suffixes = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  };

  // Helper function to get month name
  const getMonthName = (monthNum: number): string => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                   "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months[monthNum - 1] || "Invalid";
  };

  // Helper function to get day name
  const getDayName = (dayNum: number): string => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return days[dayNum] || "Invalid";
  };

  // Helper function to format time
  const formatTime = (h: number, m: number): string => {
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const minuteStr = m.toString().padStart(2, '0');
    return `${hour12}:${minuteStr} ${ampm}`;
  };

  // Parse wildcards vs specific values
  const isWildcard = (part: string) => part === '*';
  const parseNumber = (part: string) => parseInt(part, 10);

  // Every minute
  if (isWildcard(minute) && isWildcard(hour) && isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(dayOfWeek)) {
    return 'Every minute';
  }

  // Every hour at specific minute
  if (!isWildcard(minute) && isWildcard(hour) && isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(dayOfWeek)) {
    const m = parseNumber(minute);
    return `Every hour at minute ${m}`;
  }

  // Daily at specific time
  if (!isWildcard(minute) && !isWildcard(hour) && isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(dayOfWeek)) {
    const h = parseNumber(hour);
    const m = parseNumber(minute);
    return `Every day at ${formatTime(h, m)}`;
  }

  // Weekly on specific day at specific time
  if (!isWildcard(minute) && !isWildcard(hour) && isWildcard(dayOfMonth) && isWildcard(month) && !isWildcard(dayOfWeek)) {
    const h = parseNumber(hour);
    const m = parseNumber(minute);
    const d = parseNumber(dayOfWeek);
    return `Every ${getDayName(d)} at ${formatTime(h, m)}`;
  }

  // Monthly on specific day at specific time
  if (!isWildcard(minute) && !isWildcard(hour) && !isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(dayOfWeek)) {
    const h = parseNumber(hour);
    const m = parseNumber(minute);
    const dom = parseNumber(dayOfMonth);
    return `Every month on the ${getOrdinal(dom)} at ${formatTime(h, m)}`;
  }

  // Yearly on specific month, day at specific time
  if (!isWildcard(minute) && !isWildcard(hour) && !isWildcard(dayOfMonth) && !isWildcard(month) && isWildcard(dayOfWeek)) {
    const h = parseNumber(hour);
    const m = parseNumber(minute);
    const dom = parseNumber(dayOfMonth);
    const mon = parseNumber(month);
    return `Every year on ${getMonthName(mon)} ${getOrdinal(dom)} at ${formatTime(h, m)}`;
  }

  // Handle some common special cases
  if (minute === '0' && hour === '0' && isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(dayOfWeek)) {
    return 'Every day at midnight';
  }

  if (minute === '0' && hour === '12' && isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(dayOfWeek)) {
    return 'Every day at noon';
  }

  // Fallback for complex expressions
  try {
    let result = 'At ';
    
    if (!isWildcard(minute) && !isWildcard(hour)) {
      const h = parseNumber(hour);
      const m = parseNumber(minute);
      result += formatTime(h, m);
    } else if (!isWildcard(hour)) {
      result += `${parseNumber(hour)}:00`;
    } else if (!isWildcard(minute)) {
      result += `minute ${parseNumber(minute)} of every hour`;
    }

    if (!isWildcard(dayOfWeek)) {
      result += ` on ${getDayName(parseNumber(dayOfWeek))}`;
    }

    if (!isWildcard(dayOfMonth)) {
      result += ` on the ${getOrdinal(parseNumber(dayOfMonth))}`;
    }

    if (!isWildcard(month)) {
      result += ` in ${getMonthName(parseNumber(month))}`;
    }

    return result.trim();
  } catch (error) {
    return `Complex schedule: ${cronExpr}`;
  }
};

// Live Agent Display Name Generator - Student Number Increment Only
let studentCounter = 101; // Starting from 101 as requested
const conversationDisplayNames = new Map<string, string>(); // Maps conversation_id to display name
const usedDisplayNames = new Set<string>();

const generateDisplayName = (notification: any): string => {
  // Extract conversation ID from different possible locations
  const conversationId = notification?.conversation_id || notification?.sessionId || notification?.conversationId;
  
  // If no name available, check if we already have a display name for this conversation
  if (conversationId && conversationDisplayNames.has(conversationId)) {
    return conversationDisplayNames.get(conversationId)!;
  }
  
  // Generate a new display name for new conversations
  // Find the lowest available student number starting from 101
  let displayName = '';
  let studentNumber = 1;
  
  while (usedDisplayNames.has(`Guest User ${studentNumber}`)) {
    studentNumber++;
  }
  
  displayName = `Guest User ${studentNumber}`;
  
  // Mark this display name as used
  usedDisplayNames.add(displayName);
  
  // Update the counter to the next number for future use
  studentCounter = studentNumber + 1;
  
  // Store the display name for this conversation
  if (conversationId) {
    conversationDisplayNames.set(conversationId, displayName);
  }
  
  return displayName;
};

// Clear display name for a specific conversation (manual reset if needed)
const clearConversationDisplayName = (conversationId: string) => {
  const displayName = conversationDisplayNames.get(conversationId);
  if (displayName) {
    // Remove from used names so it can be reused
    usedDisplayNames.delete(displayName);
    conversationDisplayNames.delete(conversationId);
  }
};

// Reset display name counter (useful for testing or session reset)
const resetDisplayNameCounter = () => {
  studentCounter = 101;
  conversationDisplayNames.clear();
  usedDisplayNames.clear();
};

export {
  routerHistory,
  extractIdToken,
  getUserRoleFromToken,
  getTenantNameFromToken,
  setRouterHistory,
  convertBytestoMB,
  statusChecker,
  statusCheckerWithAuth,
  uploadToLocalStorage,
  getFromLocalStorage,
  downloadBlob,
  getValueFromLocalStorage,
  addValueToLocalStorage,
  getTenantId,
  getFileType,
  generateNodeSequenceResponse,
  generateParameterListWithSchema,
  extractSchemaProperties,
  dropDownConverter,
  getAccessLists,
  FilterValues,
  pastTickets,
  tickets,
  getUserRole,
  getMonthRangeConversation,
  getMonthRange,
  getNextMonthRange,
  getNextDayRange,
  getNextWeekRange,
  getNextThreeMonthRange,
  getNextYearRange,
  getWeekRange,
  getDayRange,
  getYearRange,
  getYesterdayRange,
  healthTable,
  getTenantIdwithtokenId,
  getCurrentTimeChatBot,
  getCurrentPayloadTimeChatBot,
  uploadCheck,
  validateCSVFormat,
  CSV_VALIDATION_REQUIREMENTS,
  convertMillisToDateString,
  getFormatedDate,
  getAllValues,
  LanguageOptions,
  getQuestion,
  getThreeMonthRange,
  PaginationLimit,
  formatToPythonAcceptableName,
  getLocalTimeFromUTC,
  convertUTCToLocalTime,
  convertLocalToUTC,
  convertTimeToUTC,
  isToday,
  isYesterday,
  filteredData,
  getUserId,
  getUserName,
  getQuarterRange,
  formatUTCTime,
  alertsData,
  // Schedule utilities
  scheduleIntervalOptions,
  monthOptions,
  dayOfMonthOptions,
  dayOfWeekOptions,
  hourOptions,
  minuteOptions,
  parseCronExpression,
  generateCronExpression,
  cronToHumanReadable,
  // Live Agent utilities
  generateDisplayName,
  clearConversationDisplayName,
  resetDisplayNameCounter,
}
